/*
 * ETHOS — Bülten (09.10.2026)
 *
 * Formlar (snippets/ethos-newsletter-form, Event bölümü): normal Shopify gönderimi. Shopify bu formları hCaptcha ile
 * korur ve gönderimi kendisi yapar (görünmez doğrulama → sayfa yeniden yüklenir). Dönüşte adres
 * `?customer_posted=true#<form id>` olur; Liquid teşekkürü sayfadaki TÜM bülten formlarına basar → burada yalnız
 * gönderilen formda bırakılır (alan gizlenir, teşekkür kalır), kayıt tarayıcıda hatırlanır, adres temizlenir.
 *
 * Pop-up (sections/ethos-newsletter-popup): oturumun N. saniyesinde bir kez açılır (sayfa değişse de süre sıfırlanmaz).
 * Kapatılınca X gün, kayıttan sonra hiç gösterilmez; başka bir çekmece/pencere açıkken ya da ziyaretçi bir alana
 * yazarken bekler. Yerel modal <dialog> KULLANILMAZ: üst katmanda (top layer) durup sayfanın geri kalanını «inert»
 * yapardı — hCaptcha bulmaca gösterirse onun penceresi altta kalıp tıklanamazdı. Bunun yerine body'ye taşınan sabit
 * katman (z-index 100) + .page-wrapper inert + odak tuzağı; hCaptcha'nın penceresi (body'de, en üst z-index) üstte kalır.
 * Tema editöründe yalnız bölüm seçilince açılır.
 *
 * Modül olarak yüklenir: aynı URL birden çok kez eklense de bir kez çalışır.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';

const KEY = 'ethos-news'; // localStorage: 'subscribed' | 'dismissed:<ms>'
const T0 = 'ethos-news-t0'; // sessionStorage: oturumun ilk sayfasının zamanı

const read = (store, key) => {
  try {
    return store.getItem(key);
  } catch (e) {
    return null;
  }
};
const write = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch (e) {
    /* gizli mod vb. — yok say */
  }
};
const isSubscribed = () => read(localStorage, KEY) === 'subscribed';
const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- 1 · Gönderimden dönüş ---------- */

const posted = /[?&]customer_posted=true/.test(location.search);
const hashTarget = location.hash.length > 1 ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
const returnedForm = hashTarget && hashTarget.matches('form[data-ethos-newsletter]') ? hashTarget : null;

if (posted) write(localStorage, KEY, 'subscribed');

document.querySelectorAll('form[data-ethos-newsletter]').forEach((form) => {
  const message = form.querySelector('[data-nl-msg]');
  if (!message || !message.textContent.trim()) return;
  if (returnedForm && form !== returnedForm) {
    message.textContent = ''; // mesaj başka bir forma ait
  } else if (posted && !message.dataset.kind) {
    form.classList.add('is-done');
  }
});

if (posted) {
  // Adres çubuğunu temizle (yenileyince tekrar «teşekkür» görünmesin); çapa kaydırması bittikten sonra
  window.addEventListener('load', () => {
    setTimeout(() => {
      const params = new URLSearchParams(location.search);
      params.delete('customer_posted');
      const query = params.toString();
      history.replaceState(history.state, '', location.pathname + (query ? '?' + query : ''));
    }, 600);
  });
}

/* ---------- 2 · Pop-up ---------- */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])';

function setupPopup(popup) {
  if (!popup || popup.dataset.ready) return;
  popup.dataset.ready = '1';

  const designMode = popup.hasAttribute('data-design-mode');
  const delay = Math.max(3, Number(popup.dataset.delay) || 12) * 1000;
  const snoozeDays = Math.max(1, Number(popup.dataset.snooze) || 30);
  const panel = popup.querySelector('.ethos-pop__panel');
  const image = popup.querySelector('img');
  const form = popup.querySelector('form[data-ethos-newsletter]');
  const pageWrapper = document.querySelector('.page-wrapper');
  let lastFocus = null;
  let closing;

  // Sayfa içeriği inert yapılabilsin diye katmanı body'ye taşı (editörde bölüm yerinde kalır)
  if (!designMode) document.body.appendChild(popup);

  const onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close('dismiss');
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const open = () => {
    if (!popup.hidden && !popup.classList.contains('is-out')) return;
    clearTimeout(closing);
    lastFocus = document.activeElement;
    popup.classList.remove('is-out');
    popup.hidden = false;
    lockScroll(popup);
    if (!designMode && pageWrapper) pageWrapper.inert = true;
    document.addEventListener('keydown', onKeydown);
    panel.focus({ preventScroll: true });
    // İki kare bekle: başlangıç durumu çizilsin, sonra geçiş başlasın
    requestAnimationFrame(() => requestAnimationFrame(() => popup.classList.add('is-in')));
  };

  const close = (reason) => {
    if (popup.hidden || popup.classList.contains('is-out')) return;
    if (!designMode && reason === 'dismiss' && !isSubscribed()) write(localStorage, KEY, 'dismissed:' + Date.now());
    document.removeEventListener('keydown', onKeydown);
    popup.classList.remove('is-in');
    popup.classList.add('is-out');
    const end = () => {
      popup.hidden = true;
      popup.classList.remove('is-out');
      unlockScroll(popup);
      if (pageWrapper) pageWrapper.inert = false;
      if (lastFocus && lastFocus.isConnected && lastFocus !== document.body) lastFocus.focus({ preventScroll: true });
    };
    if (reducedMotion()) end();
    else closing = setTimeout(end, 420);
  };

  popup.addEventListener('click', (event) => {
    if (event.target.closest('[data-pop-close]')) close('dismiss');
  });

  if (designMode) {
    const mine = (event) => event.detail && event.detail.sectionId === popup.dataset.sectionId;
    document.addEventListener('shopify:section:select', (event) => mine(event) && open());
    document.addEventListener('shopify:section:deselect', (event) => mine(event) && close());
    if (popup.hasAttribute('data-open-now')) open();
    return;
  }

  // Pop-up'tan gönderildi ve geri dönüldü: teşekkürü (ya da Shopify'ın hata mesajını) göster
  if (form && form === returnedForm) {
    if (image) image.loading = 'eager';
    open();
    if (form.classList.contains('is-done')) setTimeout(() => close('done'), 3200);
    return;
  }

  if (isSubscribed()) return;
  const dismissed = /^dismissed:(\d+)$/.exec(read(localStorage, KEY) || '');
  if (dismissed && Date.now() - Number(dismissed[1]) < snoozeDays * 864e5) return;

  let t0 = Number(read(sessionStorage, T0));
  if (!t0) {
    t0 = Date.now();
    write(sessionStorage, T0, String(t0));
  }
  const wait = Math.max(delay - (Date.now() - t0), 2500);

  // Gizli katmandaki lazy görsel yüklenmez: açılıştan biraz önce yüklet
  setTimeout(() => {
    if (image) image.loading = 'eager';
  }, Math.max(wait - 2500, 0));

  const attempt = () => {
    if (isSubscribed()) return;
    const active = document.activeElement;
    const busy =
      document.hidden ||
      document.documentElement.hasAttribute('scroll-lock') ||
      document.querySelector('dialog[open]') ||
      (active && active.matches && active.matches('input, textarea, select, [contenteditable="true"]'));
    if (busy) {
      setTimeout(attempt, 4000);
      return;
    }
    if (image && !image.complete) {
      image.loading = 'eager';
      const ready = image.decode ? image.decode().catch(() => {}) : Promise.resolve();
      Promise.race([ready, new Promise((resolve) => setTimeout(resolve, 1500))]).then(open);
    } else {
      open();
    }
  };
  setTimeout(attempt, wait);
}

setupPopup(document.getElementById('EthosNewsPopup'));

// Tema editörü: bölüm ayarı değişince Shopify bölümü yeniden çizer — yeni katmanı bağla ve açık tut
document.addEventListener('shopify:section:load', () => {
  const popup = document.getElementById('EthosNewsPopup');
  if (popup && !popup.dataset.ready) {
    popup.setAttribute('data-open-now', '');
    setupPopup(popup);
  }
});
